import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus, Trash2, Edit2, AlertTriangle, Loader2, RefreshCcw,
  ArrowDownLeft, ArrowUpRight, CheckCircle2, Clock
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from './ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';
import { pagosTasasAPI } from '../api';
import { toast } from '../hooks/use-toast';

const TRAMITE_OPTS = [
  { value: 'demanda_propia', label: 'Demanda propia' },
  { value: 'recurso_reposicion', label: 'Recurso de Reposición' },
  { value: 'recurso_revision', label: 'Recurso de Revisión' },
  { value: 'recurso_apelacion', label: 'Recurso de Apelación' },
  { value: 'casacion', label: 'Casación' },
  { value: 'otro', label: 'Otro trámite' },
];
const METODO_OPTS = [
  { value: 'transferencia', label: 'Transferencia' },
  { value: 'tarjeta', label: 'Tarjeta' },
  { value: 'efectivo', label: 'Efectivo' },
  { value: 'sede_electronica', label: 'Sede electrónica' },
];
const ESTADO_OPTS = [
  { value: 'pagada', label: 'Pagada' },
  { value: 'pendiente_devolucion', label: 'Pendiente de devolución' },
  { value: 'devuelta', label: 'Devuelta' },
  { value: 'no_recuperable', label: 'No recuperable' },
];

const estadoColor = (e) => ({
  pagada: 'bg-blue-100 text-blue-700',
  pendiente_devolucion: 'bg-amber-100 text-amber-700',
  devuelta: 'bg-green-100 text-green-700',
  no_recuperable: 'bg-gray-200 text-gray-700',
}[e] || 'bg-gray-100 text-gray-700');

const toDateInput = (iso) => {
  if (!iso) return '';
  return new Date(iso).toISOString().slice(0, 10);
};

const emptyForm = () => ({
  tipo: 'pago',
  tramite: 'demanda_propia',
  metodo: '',
  estado: 'pagada',
  concepto: '',
  fecha: new Date().toISOString().slice(0, 10),
  importe: '',
  pago_origen_id: '',
  notas: '',
  fecha_pagada: '',
  fecha_pendiente_devolucion: '',
  fecha_devuelta: '',
  fecha_no_recuperable: '',
});

const PagoTasasSection = ({ expedienteId }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await pagosTasasAPI.listByExpediente(expedienteId);
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch {
      toast({ title: 'Error', description: 'No se pudieron cargar los pagos de tasas', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [expedienteId]);

  useEffect(() => { if (expedienteId) load(); }, [expedienteId, load]);

  const pagosOrigenDisponibles = items.filter(
    (p) => p.tipo === 'pago' && (editing ? p.id !== editing.id : true)
  );

  const openNew = () => { setEditing(null); setForm(emptyForm()); setDialogOpen(true); };
  const openEdit = (item) => {
    setEditing(item);
    setForm({
      tipo: item.tipo || 'pago',
      tramite: item.tramite || 'demanda',
      metodo: item.metodo || '',
      estado: item.estado || 'pagada',
      concepto: item.concepto || '',
      fecha: toDateInput(item.fecha),
      importe: item.importe ?? '',
      pago_origen_id: item.pago_origen_id ? String(item.pago_origen_id) : '',
      notas: item.notas || '',
      fecha_pagada: toDateInput(item.fecha_pagada),
      fecha_pendiente_devolucion: toDateInput(item.fecha_pendiente_devolucion),
      fecha_devuelta: toDateInput(item.fecha_devuelta),
      fecha_no_recuperable: toDateInput(item.fecha_no_recuperable),
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.importe || parseFloat(form.importe) <= 0) {
      toast({ title: 'Error', description: 'Introduce un importe válido mayor que 0', variant: 'destructive' });
      return;
    }
    if (form.tipo === 'devolucion' && !form.pago_origen_id) {
      toast({ title: 'Error', description: 'En una devolución debes vincular el pago original', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const toISO = (d) => d ? new Date(d).toISOString() : null;
      const payload = {
        tipo: form.tipo,
        tramite: form.tramite,
        metodo: form.metodo || null,
        estado: form.estado,
        concepto: form.concepto || null,
        fecha: toISO(form.fecha),
        importe: parseFloat(form.importe),
        pago_origen_id: form.pago_origen_id ? parseInt(form.pago_origen_id, 10) : null,
        notas: form.notas || null,
        fecha_pagada: toISO(form.fecha_pagada),
        fecha_pendiente_devolucion: toISO(form.fecha_pendiente_devolucion),
        fecha_devuelta: toISO(form.fecha_devuelta),
        fecha_no_recuperable: toISO(form.fecha_no_recuperable),
      };
      if (editing) {
        await pagosTasasAPI.update(editing.id, payload);
        toast({ title: 'Actualizado', description: 'Registro modificado correctamente' });
      } else {
        await pagosTasasAPI.createForExpediente(expedienteId, payload);
        toast({ title: 'Creado', description: 'Registro creado correctamente' });
      }
      setDialogOpen(false);
      await load();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'No se pudo guardar';
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await pagosTasasAPI.delete(deleteTarget.id);
      toast({ title: 'Eliminado' });
      setDeleteTarget(null);
      await load();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'No se pudo eliminar';
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    }
  };

  const totales = items.reduce((acc, p) => {
    if (p.tipo === 'pago') acc.pagado += p.importe || 0;
    if (p.tipo === 'devolucion') acc.devuelto += p.importe || 0;
    if (p.tipo === 'pago' && p.estado === 'pendiente_devolucion') acc.pendiente += p.importe || 0;
    if (p.alerta_vencida) acc.vencidos += 1;
    return acc;
  }, { pagado: 0, devuelto: 0, pendiente: 0, vencidos: 0 });

  return (
    <div className="space-y-4" data-testid="pago-tasas-section">
      {/* Resumen */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="rounded-lg border bg-blue-50 p-3">
          <div className="text-xs text-blue-700 font-medium">Total pagado</div>
          <div className="text-lg font-bold text-blue-900">{totales.pagado.toFixed(2)} €</div>
        </div>
        <div className="rounded-lg border bg-green-50 p-3">
          <div className="text-xs text-green-700 font-medium">Total devuelto</div>
          <div className="text-lg font-bold text-green-900">{totales.devuelto.toFixed(2)} €</div>
        </div>
        <div className="rounded-lg border bg-amber-50 p-3">
          <div className="text-xs text-amber-700 font-medium">Pendiente devolución</div>
          <div className="text-lg font-bold text-amber-900">{totales.pendiente.toFixed(2)} €</div>
        </div>
        <div className="rounded-lg border bg-rose-50 p-3">
          <div className="text-xs text-rose-700 font-medium flex items-center gap-1">
            <AlertTriangle className="w-3.5 h-3.5" /> Vencidas (&gt;6 meses)
          </div>
          <div className="text-lg font-bold text-rose-900">{totales.vencidos}</div>
        </div>
      </div>

      <div className="flex justify-between items-center">
        <h3 className="text-base font-semibold text-gray-800">Registros de Pagos y Devoluciones</h3>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} data-testid="btn-refresh-pagos-tasas">
            <RefreshCcw className="w-4 h-4 mr-1" /> Actualizar
          </Button>
          <Button size="sm" onClick={openNew} className="bg-cyan-600 hover:bg-cyan-700" data-testid="btn-new-pago-tasa">
            <Plus className="w-4 h-4 mr-1" /> Nuevo
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
      ) : items.length === 0 ? (
        <div className="py-10 text-center text-gray-500 border rounded-lg">
          No hay pagos de tasas registrados. Pulsa <span className="font-medium">Nuevo</span> para crear el primero.
        </div>
      ) : (
        <div className="overflow-x-auto border rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left">
              <tr>
                <th className="px-3 py-2 font-semibold text-gray-700">Fecha</th>
                <th className="px-3 py-2 font-semibold text-gray-700">Tipo</th>
                <th className="px-3 py-2 font-semibold text-gray-700">Trámite</th>
                <th className="px-3 py-2 font-semibold text-gray-700">Concepto</th>
                <th className="px-3 py-2 font-semibold text-gray-700 text-right">Importe</th>
                <th className="px-3 py-2 font-semibold text-gray-700">Estado</th>
                <th className="px-3 py-2 font-semibold text-gray-700">Método</th>
                <th className="px-3 py-2 font-semibold text-gray-700 text-center">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} className={`border-t ${p.alerta_vencida ? 'bg-rose-50' : ''}`} data-testid={`pago-tasa-row-${p.id}`}>
                  <td className="px-3 py-2 text-gray-700">{p.fecha ? new Date(p.fecha).toLocaleDateString('es-ES') : '—'}</td>
                  <td className="px-3 py-2">
                    {p.tipo === 'pago' ? (
                      <span className="inline-flex items-center gap-1 text-blue-700"><ArrowUpRight className="w-4 h-4" />Pago</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-green-700"><ArrowDownLeft className="w-4 h-4" />Devolución</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-gray-700">{p.tramite_display}</td>
                  <td className="px-3 py-2 text-gray-700 max-w-xs truncate" title={p.concepto}>{p.concepto || '—'}</td>
                  <td className="px-3 py-2 text-right font-medium text-gray-900">{(p.importe || 0).toFixed(2)} €</td>
                  <td className="px-3 py-2">
                    <Badge className={estadoColor(p.estado)}>{p.estado_display}</Badge>
                    {p.alerta_vencida && (
                      <div className="text-xs text-rose-600 flex items-center gap-1 mt-1">
                        <Clock className="w-3 h-3" /> {p.dias_pendientes} días
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-gray-600">{p.metodo_display || '—'}</td>
                  <td className="px-3 py-2 text-center">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(p)} data-testid={`btn-edit-pago-tasa-${p.id}`}>
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(p)} data-testid={`btn-delete-pago-tasa-${p.id}`}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* DIALOG CREAR/EDITAR */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar registro' : 'Nuevo registro'}</DialogTitle>
            <DialogDescription>Registro de pago o devolución de tasa del expediente.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tipo {!editing && '*'}</Label>
                {editing ? (
                  <div className="mt-1 flex items-center gap-2" data-testid="edit-tipo-readonly">
                    {form.tipo === 'devolucion' ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-blue-100 text-blue-800 text-xs font-semibold">Devolución</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-semibold">Pago</span>
                    )}
                    <span className="text-xs text-gray-500">
                      {form.tipo === 'devolucion' && editing?.pago_origen_id
                        ? `Devolución vinculada al pago #${editing.pago_origen_id}.`
                        : 'Para marcar como devuelta, usa el botón "Registrar devolución" del listado.'}
                    </span>
                  </div>
                ) : (
                  <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v, pago_origen_id: v === 'pago' ? '' : form.pago_origen_id })}>
                    <SelectTrigger data-testid="select-tipo"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pago">Pago (nueva tasa)</SelectItem>
                      <SelectItem value="devolucion">Devolución (ingreso de un pago anterior)</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>
              <div>
                <Label>Trámite *</Label>
                <Select value={form.tramite} onValueChange={(v) => setForm({ ...form, tramite: v })}>
                  <SelectTrigger data-testid="select-tramite"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TRAMITE_OPTS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Concepto</Label>
              <Input value={form.concepto} onChange={(e) => setForm({ ...form, concepto: e.target.value })}
                     placeholder="Ej. Tasa demanda principal, Recurso apelación…" />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Fecha *</Label>
                <Input type="date" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} required />
              </div>
              <div>
                <Label>Importe (€) *</Label>
                <Input type="number" step="0.01" min="0" value={form.importe}
                       onChange={(e) => setForm({ ...form, importe: e.target.value })}
                       placeholder="0.00" required data-testid="input-importe" />
              </div>
              <div>
                <Label>Método</Label>
                <Select value={form.metodo || 'none'} onValueChange={(v) => setForm({ ...form, metodo: v === 'none' ? '' : v })}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin especificar</SelectItem>
                    {METODO_OPTS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Estado *</Label>
                <Select value={form.estado} onValueChange={(v) => setForm({ ...form, estado: v })}>
                  <SelectTrigger data-testid="select-estado"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ESTADO_OPTS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {form.tipo === 'devolucion' && (
                <div>
                  <Label>Pago original vinculado *</Label>
                  <Select value={form.pago_origen_id || 'none'}
                          onValueChange={(v) => setForm({ ...form, pago_origen_id: v === 'none' ? '' : v })}>
                    <SelectTrigger data-testid="select-pago-origen"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Ninguno —</SelectItem>
                      {pagosOrigenDisponibles.map(p => (
                        <SelectItem key={p.id} value={String(p.id)}>
                          #{p.id} · {p.tramite_display} · {(p.importe || 0).toFixed(2)}€ · {p.fecha ? new Date(p.fecha).toLocaleDateString('es-ES') : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">Obligatorio para trazabilidad.</p>
                </div>
              )}
            </div>

            <div className="border rounded-md p-3 bg-gray-50">
              <div className="text-xs font-semibold text-gray-700 mb-2">Fechas de cambio de estado (timings)</div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Fecha pagada</Label>
                  <Input type="date" value={form.fecha_pagada || ''} onChange={(e) => setForm({ ...form, fecha_pagada: e.target.value })} /></div>
                <div><Label className="text-xs">Fecha pendiente devolución</Label>
                  <Input type="date" value={form.fecha_pendiente_devolucion || ''} onChange={(e) => setForm({ ...form, fecha_pendiente_devolucion: e.target.value })} /></div>
                <div><Label className="text-xs">Fecha devuelta</Label>
                  <Input type="date" value={form.fecha_devuelta || ''} onChange={(e) => setForm({ ...form, fecha_devuelta: e.target.value })} /></div>
                <div><Label className="text-xs">Fecha no recuperable</Label>
                  <Input type="date" value={form.fecha_no_recuperable || ''} onChange={(e) => setForm({ ...form, fecha_no_recuperable: e.target.value })} /></div>
              </div>
              <p className="text-[11px] text-gray-500 mt-2">Al cambiar el "Estado" se rellena automáticamente la fecha si está vacía. Puedes corregir cualquier fecha manualmente.</p>
            </div>

            <div>
              <Label>Notas</Label>
              <Textarea rows={3} value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })}
                        placeholder="Información adicional, referencias, etc." />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" className="bg-cyan-600 hover:bg-cyan-700" disabled={saving} data-testid="btn-save-pago-tasa">
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {editing ? 'Guardar cambios' : 'Crear registro'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ALERT DELETE */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar este registro?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará el registro de {deleteTarget?.tipo_display} de {(deleteTarget?.importe || 0).toFixed(2)}€.
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              <CheckCircle2 className="w-4 h-4 mr-1" /> Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PagoTasasSection;
